import React from 'react';
import { motion, Variants } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import GeometricBackground from './GeometricBackground';

const Hero: React.FC = () => {
  const { t } = useTranslation();

  const textVariants: Variants = {
    initial: { opacity: 0, y: 50 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.8, staggerChildren: 0.2 } },
  };

  const buttonVariants: Variants = {
    initial: { scale: 0 },
    animate: { scale: 1, transition: { type: "spring", stiffness: 260, damping: 20 } },
  };

  const floatingVariants: Variants = {
    initial: {
      opacity: 0,
      scale: 1.1,
      transition: { duration: 0.5, ease: "easeOut" }
    },
    animate: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const handleButtonClick = () => {
    const projectsSection = document.getElementById('projects');
    if (projectsSection) {
      const headerHeight = 80; // Adjust this value based on your header's actual height
      const elementPosition = projectsSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  return (
    <section className="relative h-screen flex items-center justify-center overflow-hidden" id="home">
      <div className="absolute inset-0 z-0 animated-gradient opacity-50">
        <div className="absolute inset-0 opacity-10 animated-pattern" />
      </div>
      <GeometricBackground />
      <motion.div
        className="relative z-10 text-center text-white px-4"
        variants={textVariants}
        initial="initial"
        animate="animate"
      >
        <motion.h1
          className="text-6xl font-bold mb-4 leading-tight text-gradient"
          variants={textVariants}
        >
          {t('hero.title')}
        </motion.h1>
        <motion.p
          className="text-2xl mb-8 max-w-2xl mx-auto"
          variants={textVariants}
        >
          {t('hero.subtitle')}
        </motion.p>
        <motion.button
          className="bg-white text-blue-500 px-8 py-3 rounded-full text-lg font-semibold hover:bg-blue-100 transition-colors shadow-lg shadow-neon"
          variants={buttonVariants}
          whileHover={{ scale: 1.05, boxShadow: "0 0 25px rgba(255,255,255,0.5)" }}
          whileTap={{ scale: 0.95 }}
          onClick={handleButtonClick}
        >
          {t('hero.cta')}
        </motion.button>
      </motion.div>
      <motion.div
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        variants={floatingVariants}
        animate="animate"
      >
        <motion.div
          className="animate-bounce"
          whileHover={{ scale: 1.2 }}
        >
          <svg className="w-6 h-6 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
            <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
          </svg>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;